import { useEffect, useRef, useState } from 'react'
import { Icon } from '@/components/ds'
import { PlayCircle } from '@/components/ds/icons'
import { cn } from '@/lib/utils'
import {
  GRUPOS_TUTORIALES,
  TUTORIALES,
  formatearDuracion,
  urlEmbed,
  urlMiniatura,
  type Tutorial,
} from '@/lib/tutoriales'

/**
 * Página pública de tutoriales en video.
 *
 * Se define como ruta pública (fuera de AppLayout, junto a /terminos y
 * /privacidad) por dos razones:
 *  1. fc-app abre esta misma URL con Browser.open, y el móvil no comparte la
 *     sesión del panel — necesita una página que renderice sin autenticación.
 *  2. El panel la abre en una pestaña nueva, y la sesión web vive en
 *     sessionStorage por pestaña: esa pestaña tampoco está autenticada.
 *
 * Los videos son públicos, así que exponerlos sin login no filtra nada.
 *
 * NO lleva enlace "Volver al panel": la pestaña acompaña al panel, no lo
 * reemplaza. El usuario mira el video acá y prueba en la otra pestaña, que
 * sigue abierta y con su sesión intacta; un "volver" lo mandaría al login.
 *
 * FORMA: un reproductor grande + la lista al costado, en vez de una grilla de
 * videos del mismo tamaño. Son grabaciones de pantalla: en una tarjeta de 340px
 * no se lee lo que el mouse está apretando, que es justamente lo que el usuario
 * vino a ver. El reproductor queda fijo (sticky) mientras se recorre la lista.
 */
export function Tutoriales() {
  const [seleccionado, setSeleccionado] = useState<Tutorial>(TUTORIALES[0])
  // Separado de `seleccionado`: al entrar mostramos la miniatura del primero,
  // no su reproductor. Un video que arranca solo en una pestaña nueva es una
  // sorpresa desagradable, sobre todo con audio.
  const [reproduciendo, setReproduciendo] = useState(false)
  const reproductorRef = useRef<HTMLDivElement>(null)

  // La pestaña vive en paralelo al panel y el usuario alterna entre las dos:
  // si ambas dicen "Facil Control" no distingue cuál es cuál en la barra.
  useEffect(() => {
    const anterior = document.title
    document.title = 'Tutoriales · Facil Control'
    return () => {
      document.title = anterior
    }
  }, [])

  function elegir(video: Tutorial) {
    setSeleccionado(video)
    setReproduciendo(true)

    // En pantallas angostas la lista va DEBAJO del reproductor: sin esto, el
    // video arranca fuera de la vista y sólo se oye. En pantallas anchas el
    // reproductor es sticky y siempre está visible, así que no se toca.
    const caja = reproductorRef.current?.getBoundingClientRect()
    if (caja && caja.top < 0) {
      reproductorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="bg-canvas min-h-screen">
      <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-[26px] px-7 py-9">
        <header className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-primary text-primary-foreground flex h-10 w-10 items-center justify-center rounded-xl">
              <PlayCircle className="h-5 w-5" />
            </div>
            <span className="text-lg font-bold tracking-tight">Facil Control</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <h1 className="fc-page-title">Tutoriales en video</h1>
            <p className="fc-page-subtitle max-w-2xl">
              Videos cortos, paso a paso. Deja esta pestaña abierta y prueba en la otra.
            </p>
          </div>
        </header>

        <div className="grid items-start gap-[18px] lg:grid-cols-[minmax(0,1fr)_352px]">
          <div ref={reproductorRef} className="flex flex-col gap-3 lg:sticky lg:top-9">
            <div className="bg-secondary relative aspect-video overflow-hidden rounded-2xl border">
              {reproduciendo ? (
                <iframe
                  // La key remonta el iframe al cambiar de video: sin ella,
                  // React reusa el nodo y YouTube ignora el src nuevo.
                  key={seleccionado.id}
                  className="absolute inset-0 h-full w-full"
                  src={urlEmbed(seleccionado.id)}
                  title={seleccionado.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <BotonReproducir
                  video={seleccionado}
                  onReproducir={() => setReproduciendo(true)}
                  tamanoIcono={64}
                />
              )}
            </div>

            <div className="flex items-start justify-between gap-3">
              <h2 className="fc-section-title">{seleccionado.titulo}</h2>
              <span className="fc-meta fc-num shrink-0 pt-0.5">
                {formatearDuracion(seleccionado.duracion)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-[18px]">
            {GRUPOS_TUTORIALES.map((grupo) => {
              const videos = TUTORIALES.filter((v) => v.plataforma === grupo.plataforma)
              if (videos.length === 0) return null

              return (
                <section key={grupo.plataforma} className="flex flex-col gap-2">
                  <div className="flex flex-col gap-0.5">
                    <h3 className="fc-section-title">{grupo.titulo}</h3>
                    <p className="text-meta text-[12.5px] leading-[1.45]">
                      {grupo.descripcion}
                    </p>
                  </div>

                  <ul className="flex flex-col gap-1.5">
                    {videos.map((video) => (
                      <li key={video.id}>
                        <ItemLista
                          video={video}
                          activo={video.id === seleccionado.id}
                          onElegir={() => elegir(video)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              )
            })}
          </div>
        </div>

        <p className="text-meta text-center text-[11px]">
          © {new Date().getFullYear()} Facil Control. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}

/**
 * Miniatura con el play encima, en lugar del iframe.
 *
 * Cada reproductor de YouTube arrastra cerca de un mega de scripts: montarlo
 * recién al apretar play deja la página liviana aunque el catálogo crezca.
 */
function BotonReproducir({
  video,
  onReproducir,
  tamanoIcono,
}: {
  video: Tutorial
  onReproducir: () => void
  tamanoIcono: number
}) {
  return (
    <button
      type="button"
      onClick={onReproducir}
      aria-label={`Reproducir: ${video.titulo}`}
      className="group focus-visible:outline-ring absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <img
        src={urlMiniatura(video.id)}
        data-video={video.id}
        onError={caerAMiniaturaHq}
        alt=""
        className="h-full w-full object-cover"
      />
      {/* Velo navy: sube el contraste del play sobre miniaturas claras y marca
          el hover sin mover nada de lugar. */}
      <span className="bg-secondary/25 group-hover:bg-secondary/40 absolute inset-0 flex items-center justify-center transition-colors">
        <Icon name="play_circle" size={tamanoIcono} className="text-white" />
      </span>
    </button>
  )
}

/** Fila de la lista. Selección con el patrón único del DS: borde teal + fondo `--primary-50`. */
function ItemLista({
  video,
  activo,
  onElegir,
}: {
  video: Tutorial
  activo: boolean
  onElegir: () => void
}) {
  return (
    <button
      type="button"
      onClick={onElegir}
      aria-current={activo ? 'true' : undefined}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-xl border p-2 text-left transition-colors',
        activo
          ? 'border-primary bg-primary-50'
          : 'bg-card hover:bg-primary-50 hover:border-primary-border',
      )}
    >
      <img
        src={urlMiniatura(video.id, 'mq')}
        alt=""
        loading="lazy"
        className="bg-secondary h-[52px] w-[92px] shrink-0 rounded-md object-cover"
      />
      <span className="flex min-w-0 flex-col gap-1">
        <span
          className={cn(
            'fc-label leading-snug',
            activo && 'text-primary-strong font-semibold',
          )}
        >
          {video.titulo}
        </span>
        <span className={cn('fc-meta fc-num', activo && 'text-primary-hover')}>
          {formatearDuracion(video.duracion)}
        </span>
      </span>
    </button>
  )
}

/** `maxresdefault` no existe para todos los videos; `hqdefault` sí. */
function caerAMiniaturaHq(evento: React.SyntheticEvent<HTMLImageElement>) {
  const img = evento.currentTarget
  if (img.dataset.fallback) return
  img.dataset.fallback = '1'
  img.src = urlMiniatura(img.dataset.video ?? '', 'hq')
}
